<script setup lang="ts">
import IconDate from "@/icons/IconDate.vue";
import {computed, ref, toRef, toRefs, watch} from "vue";
import {useCartStore, type LineItemWithAddressAndRates} from "@/stores/cartStore";
import DatePickerDay from "@/components/DatePickerDay.vue";
import {dateToFormattedString, dateToString, formatDateToUS, makeDateFromString} from "@/utils/dateUtils";
import {useDatePicker} from "@/composables/useDatePicker";

const firstTimeSelect = ref(true);
const hoverDate = ref(null);
const cartStore = useCartStore();
const properties = defineProps<{
  lineItem: LineItemWithAddressAndRates
}>();
const lineItem = toRef(properties, 'lineItem');
const rates = computed(() => lineItem.value.rates);
const clickedDate = ref<Date | null>(lineItem.value.selectedRate && lineItem.value.selectedRate.estimatedDate ? Array.isArray(lineItem.value.selectedRate.estimatedDate)  ? lineItem.value.selectedRate.estimatedDate[1] : lineItem.value.selectedRate.estimatedDate : null);

const showCalendar = ref(false);
const debugDate = ref(new Date());
const calendarRef = ref<any | null>(null);
const dontDisableSaturday = computed(() => !isExactDayDelivery.value && (lineItem.value.selectedAddress.type === "Residential" && lineItem.value.weight <= 1))
const initialDate = computed(() => cartStore.isDebug ? debugDate.value : new Date());

const hasGroundDelivery = computed(() => {
  const groundShipping = lineItem.value.rates.find(i => !i.delivery_days.every(day => day === 1) && !i.delivery_days.every(day => day === 2) && i.delivery_days.length === 2);
  return Boolean(groundShipping && groundShipping.delivery_days.length !== 2);
});

const isExactDayDelivery = ref(lineItem.value.selectedAddress && hasGroundDelivery.value ? true : lineItem.value.selectedRate && lineItem.value.selectedRate.isExactDayDelivery ? lineItem.value.selectedRate.isExactDayDelivery : false);

const disabledDates = computed(() => {
  const dates = [];

  if(cartStore.max_date) {
    dates.push([
      cartStore.max_date, null
    ]);
  }

  if(cartStore.disabled_delivery_dates && cartStore.disabled_delivery_dates.length) {
    dates.push(...cartStore.disabled_delivery_dates);
  }

  return dates;
});

const {
  getDayWithOffset,
  firstAvailableShippingDate,
  calculateRange,
  selectedDates,
  exactDayDelivery,
  exactDayShippingDate,
  calcApplyDate,
  rangeShippingDate,
  isNextDaySelected,
  isSecondDaySelected,
  nextDayDelivery,
  secondDayDelivery,
  groundDelivery,
} = useDatePicker({
  initialDate,
  disabledShippingDates: cartStore.disabled_shipping_dates,
  disabledDeliveryDates: cartStore.disabled_delivery_dates,
  selectedAddress: lineItem.value.selectedAddress,
  itemWeight: lineItem.value.weight,
  isExactDayDelivery,
  cutOffTime: cartStore.cutoff_time,
  minDate: lineItem.value.min_date,
  clickedDate,
  maxDate: cartStore.max_date,
  rates
});


const handleApplyDate = () => {
  if(!clickedDate.value) {
    return;
  }

  const { shipDate, estimatedDate, rate } = calcApplyDate();

  if(shipDate && estimatedDate && rate) {

    cartStore.saveShipping({
      key: lineItem.value.key,
      isExactDayDelivery: isExactDayDelivery.value,
      estimatedDate,
      shipDate,
      rate,
      clickedDate: dateToString(clickedDate.value)
    });

    showCalendar.value = false;
  }
}

watch(groundDelivery, (value) => {
  if(!value) {
    isExactDayDelivery.value = true;
  }
});

watch(lineItem, () => {
  if(lineItem.value.selectedAddress && !groundDelivery.value) {
    isExactDayDelivery.value = true;
  }
});

watch(showCalendar, (value) => {
  if(value && lineItem.value.selectedAddress && !groundDelivery.value) {
    isExactDayDelivery.value = true;
  }
});

watch(isExactDayDelivery, (value) => {
  if(value && clickedDate.value && (clickedDate.value.getDay() === 0 || clickedDate.value.getDay() === 6 || disabledDates.value.findIndex(i => !Array.isArray(i) && clickedDate.value && dateToString(i) === dateToString(clickedDate.value)) !== -1 )) {
    clickedDate.value = getDayWithOffset(clickedDate.value, 1);
  }
});

const hoveredDates = computed(() => {
  if(!hoverDate.value) {
    return [];
  }

  if( (!groundDelivery.value || groundDelivery.value && !groundDelivery.value.isOneTwo) && (nextDayDelivery.value && dateToString(hoverDate.value) === dateToString(nextDayDelivery.value.day) || secondDayDelivery.value &&dateToString(hoverDate.value) === dateToString(secondDayDelivery.value.day))) {
    return [hoverDate.value, hoverDate.value];
  }

  return calculateRange(hoverDate.value);
});

const handleCloseCalendar = () => {
  isExactDayDelivery.value = false;
  firstTimeSelect.value = true;
  clickedDate.value = lineItem.value?.selectedRate?.clickedDate ? makeDateFromString(lineItem.value?.selectedRate?.clickedDate) : null;
  showCalendar.value = false;
}

const selectedDateTextNumbers = computed(() => {
  if(lineItem.value.selectedRate && lineItem.value.selectedRate.estimatedDate) {
    if(Array.isArray(lineItem.value.selectedRate.estimatedDate)) {
      return formatDateToUS(lineItem.value.selectedRate.estimatedDate[0]) + "-" + formatDateToUS(lineItem.value.selectedRate.estimatedDate[1]);
    } else {
      return formatDateToUS(lineItem.value.selectedRate.estimatedDate)
    }
  }

  return "Select delivery date";
});

watch(calendarRef, (value) => {
  const date = new Date();
  if(value && calendarRef.value) {
    calendarRef.value.move({ month: date.getMonth()+1, year: date.getFullYear() });
  }
});

</script>
<template>
  <template v-if="cartStore.isDebug">
    <input type="text" :value="debugDate.toLocaleString('en-US', { hour12: false })" @change="(event: Event) => debugDate = new Date((event.currentTarget as HTMLInputElement).value)"/>
    <br>
    IS DEBUG = {{ cartStore?.isDebug }}<br>

    DEBUG DATE = {{ debugDate && dateToString(debugDate) }}<br>
    CUTOFF TIME = {{ cartStore.cutoff_time }}<br>
    EST DEBUG DATE = {{ debugDate.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false }) }}<br>
    clickedDate = {{ clickedDate && dateToString(clickedDate) }}<br>
    nextDayDelivery = {{ nextDayDelivery?.day && dateToString(nextDayDelivery?.day) }}<br>
    secondDayDelivery = {{ secondDayDelivery?.day && dateToString(secondDayDelivery?.day) }}<br>
    groundDelivery = {{ groundDelivery?.days }}<br>
    firstAvailableShippingDate = {{ firstAvailableShippingDate && dateToString(firstAvailableShippingDate) }}<br>
    selectedDates = {{ selectedDates?.map(i => dateToString(i)) }}<br>
    Calculated shipping date {{ lineItem?.selectedRate?.shipDate && dateToString(lineItem?.selectedRate?.shipDate) }}<br>
    <br>
    Packages {{ cartStore.packages }}<br>
    <br>
    disabled_shipping_dates {{ cartStore?.disabled_shipping_dates.map(i => dateToString(i)) }}<br>
    <br>
    disabled_delivery_dates {{ cartStore?.disabled_delivery_dates.map(i => dateToString(i)) }}<br>
  </template>

  <div class="steps__item">
    <div class="steps__head">
      <div class="steps__head-number">2</div>
      <div class="steps__head-text">
        Delivery Date
        <span v-if="lineItem?.errors?.date" class="form__label-error is-error">* Select delivery date</span>
      </div>
    </div>
    <div class="steps__body steps__body--calendar">
      <div class="select-date">
        <div class="select-date__text">Select <strong>DELIVERY DATE*</strong></div>
        <div class="select-date__choice" @click="showCalendar = true">
          <div class="select-date__input">
            {{ selectedDateTextNumbers }}
          </div>
          <IconDate />
        </div>
      </div>

      <div class="delivery-calendar" v-if="showCalendar" @click="handleCloseCalendar">
        <div class="delivery-calendar__bg" @click.stop>
          <div class="delivery-calendar__top">
            <div class="delivery-calendar__title">Delivery Date</div>
            <button class="delivery-calendar__close" type="button" aria-label="Close calendar" @click="handleCloseCalendar">
              <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M14 2L2 14" stroke="#333333" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
                <path d="M2 2L14 14" stroke="#333333" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="delivery-calendar__body">
            <div class="delivery-calendar__calendar">
              <VCalendar
                :rows="2"
                :step="1"
                :max-date="cartStore.max_date"
                :min-date="getDayWithOffset(firstAvailableShippingDate, 1, 'delivery')"
                :disabled-dates="disabledDates"
                range
                expanded
                trim-weeks
                borderless
                ref="calendarRef"
              >
                <template #day-content="{ day, dayTitle }">
                  <DatePickerDay
                    :day="day"
                    :clicked-date="clickedDate"
                    :day-title="dayTitle"
                    :hovered-dates="hoveredDates"
                    :selected-dates="selectedDates"
                    :ground-delivery="groundDelivery"
                    :next-day-delivery="nextDayDelivery"
                    :second-day-delivery="secondDayDelivery"
                    :first-available-shipping-date="firstAvailableShippingDate"
                    :exact-day-shipping-date="exactDayShippingDate"
                    :is-exact-day-delivery="isExactDayDelivery"
                    :range-shipping-date="rangeShippingDate"
                    :dont-disable-saturday="dontDisableSaturday"
                    :disabled-shipping-dates="cartStore.disabled_shipping_dates"
                    :disabled-delivery-dates="cartStore.disabled_delivery_dates"
                    :exactDisabled="calculateRange(day.date)?.length === 0"
                    @click-day="date =>  {
                      if(firstTimeSelect) {
                        if((!groundDelivery || groundDelivery && !groundDelivery.isOneTwo) && ( (nextDayDelivery && dateToString(date) === dateToString(nextDayDelivery.day) || secondDayDelivery && dateToString(date) === dateToString(secondDayDelivery.day)))) {
                          isExactDayDelivery = true;
                        }
                        firstTimeSelect = false;
                      }
                      clickedDate = date
                    }"
                    @mouse-in="date => { hoverDate = date } "
                    @mouse-out="_ => { hoverDate = null }"
                  />
                </template>
              </VCalendar>
            </div>
            <div class="delivery-calendar__info">
              <template v-if="!selectedDates.length">
                <div class="delivery-calendar__subtitle">Select Delivery Date</div>
                <div class="delivery-calendar__text">Please select a date on the calendar </div>
              </template>
              <template v-else-if="selectedDates.length === 1">
                <div class="delivery-calendar__subtitle">Estimated Delivery Date:</div>
                <div class="delivery-calendar__text">Arrives on: {{ dateToFormattedString(selectedDates[0]) }}</div>

                <div class="delivery-calendar__subtitle">Shipping Method:</div>
                <template v-if="isNextDaySelected && nextDayDelivery">
                  <div class="delivery-calendar__text">Next Day Delivery - ${{ nextDayDelivery.price }}</div>
                </template>
                <template v-else-if="isSecondDaySelected && secondDayDelivery">
                  <div class="delivery-calendar__text">Two Day Delivery - ${{ secondDayDelivery.price }}</div>
                </template>
                <template v-else-if="isExactDayDelivery && exactDayDelivery">
                  <div class="delivery-calendar__text">Exact Day Delivery - ${{ exactDayDelivery.price }}</div>
                </template>
              </template>
              <template v-else-if="groundDelivery">
                <div class="delivery-calendar__subtitle">Estimated Delivery Date:</div>
                <div class="delivery-calendar__text">Arrives Between: {{ dateToFormattedString(selectedDates[0]) }} and {{ dateToFormattedString(selectedDates[1]) }}</div>

                <div class="delivery-calendar__subtitle">Shipping Method:</div>
                <div class="delivery-calendar__text">Ground Shipping - {{ groundDelivery.price ? `$`+groundDelivery.price : 'Free' }}<br>Delivery Days - {{ groundDelivery.days[0] }}-{{ groundDelivery.days[1] }}</div>
              </template>

              <template v-if="clickedDate">
                <template v-if="groundDelivery">
                  <div class="delivery-calendar__subtitle">Need delivery on an exact day?</div>
                  <div class="delivery-calendar__checkbox">
                    <label class="calendar-checkbox">
                      <input class="calendar-checkbox__input" type="checkbox" aria-label="Need Arrival on Exact Date?" v-model="isExactDayDelivery">
                      <span class="calendar-checkbox__trigger">
                  <svg class="calendar-checkbox__icon" aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33333 9.33333L6 11.3333L12 4" stroke="white" stroke-width="1.5"/>
                  </svg>
                </span>
                      <span class="calendar-checkbox__text">Exact Day Delivery </span>
                    </label>
                  </div>
                </template>
                <div class="delivery-calendar__buttons">
                  <button class="Button Button--primary Button--full" type="button" @click="handleApplyDate">Apply Date</button>
                  <button class="Button Button--ghosted Button--full" type="button" @click="handleCloseCalendar">CANCEL</button>
                </div>
              </template>

              <div class="delivery-calendar__legend">
                <div class="delivery-calendar__subtitle">Shipping</div>
                <ul class="delivery-legend">
                  <li class="delivery-legend__item">
                    <div class="delivery-legend__icon"></div>
                    <div class="delivery-legend__text">
                      Standard Ground Shipping
                    </div>
                  </li>
                  <li class="delivery-legend__item">
                    <div class="delivery-legend__icon delivery-legend__icon--gold"></div>
                    <div class="delivery-legend__text">
                      Expedited Shipping<br>
                      (Next Day Delivery, Two Day Delivery or Saturday Delivery)
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>